// src/lib/capture/types.ts — CBAM mobile capture (Phase 0/1)
//
// Shared types + the pinned disclosure version for the third-party-audio
// capture pipeline. These are consumed by both the capture wrappers in this
// directory (Agent A) and the recorder/review UI (Agent B), so the shapes here
// are CONTRACTS — keep them in lockstep with:
//   - supabase/functions/_shared/consent-guard.ts (CURRENT_DISCLOSURE_VERSION)
//   - the research_create_mobile_source RPC argument list.

/**
 * The disclosure copy/version the recorder must have shown the capturer at
 * consent time. MUST equal CURRENT_DISCLOSURE_VERSION in
 * supabase/functions/_shared/consent-guard.ts. Bump both together.
 */
export const CURRENT_DISCLOSURE_VERSION = "2026-07-01.v1";

/**
 * Legal basis under which a third party's voice/likeness was captured.
 *  - all_parties_verbal: every party gave spoken consent (captured on the recording)
 *  - written_on_file:    signed/written consent is held outside the capture
 *  - public_seminar:     captured at a public event with no expectation of privacy
 *  - internal_only:      staff-only material; not third-party audio
 */
export type ConsentBasis =
  | "all_parties_verbal"
  | "written_on_file"
  | "public_seminar"
  | "internal_only";

/**
 * Consent envelope attached to a mobile source at creation time. Mirrors the
 * `_basis` / `_parties` / ... arguments accepted by research_create_mobile_source.
 */
export interface Consent {
  /** Legal basis for capturing third-party voice/likeness. */
  basis: ConsentBasis;
  /** Named parties present / consenting (free-text identifiers). */
  parties: string[];
  /** Jurisdiction whose consent rules apply (e.g. "GB", "US-CA"). */
  jurisdiction: string;
  /** Disclosure version shown at consent time; compare to CURRENT_DISCLOSURE_VERSION. */
  disclosureVersion: string;
  /** True if the recording itself contains the verbal-consent segment. */
  verbalSegmentPresent: boolean;
  /** True if downstream emotion/affect inference is permitted for this source. */
  emotionInferenceOk: boolean;
}
